import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { Divider } from "antd";
import apiClient from "../../../helpers/apiClient";
import {
    formatMoney,
    formatTripDateTime,
    getCarDisplayName,
    getInvoiceDue,
    getInvoiceTotal,
    getReservationLocationLabel,
    getTotalPaid,
} from "../utils/reservationHelpers";

const getCarImage = (reservation, carDetails) =>
    carDetails?.images?.image1 ||
    reservation?.carSelected?.image ||
    reservation?.carSelected?.images?.image1 ||
    reservation?.image ||
    reservation?.category?.image ||
    "";

const getCategoryName = (reservation) =>
    reservation?.carSelected?.category?.categoryName ||
    reservation?.carSelected?.categoryName ||
    reservation?.category?.categoryName ||
    reservation?.carType ||
    "";

const CheckInSummaryCard = ({ reservation, t }) => {
    const [locations, setLocations] = useState([]);
    const [carDetails, setCarDetails] = useState(null);

    useEffect(() => {
        let cancelled = false;

        const loadLocations = async () => {
            try {
                const { data } = await apiClient.get("/api/location");
                if (!cancelled && data?.success && Array.isArray(data.data)) {
                    setLocations(data.data);
                }
            } catch {
                if (!cancelled) setLocations([]);
            }
        };

        loadLocations();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const vin = reservation?.carSelected?.VIN;
        if (!vin) {
            setCarDetails(null);
            return undefined;
        }

        let cancelled = false;

        const fetchCar = async () => {
            try {
                const { data } = await apiClient.get(`/api/car/${vin}`);
                if (!cancelled && data?.success) {
                    setCarDetails(data.data);
                }
            } catch {
                if (!cancelled) setCarDetails(null);
            }
        };

        fetchCar();
        return () => {
            cancelled = true;
        };
    }, [reservation?.carSelected?.VIN]);

    const carName = getCarDisplayName(reservation);
    const carImage = getCarImage(reservation, carDetails);
    const plateNumber = reservation?.carSelected?.carNumber || carDetails?.carNumber || "";
    const categoryName = getCategoryName(reservation);
    const pickup = reservation?.exactPickupTime || reservation?.pickupDate;
    const dropoff = reservation?.exactDropoffTime || reservation?.dropoffDate;
    const locationLabel = getReservationLocationLabel(reservation, locations);
    const locationLines = useMemo(() => {
        if (!locationLabel || locationLabel === "—") return ["—"];
        return locationLabel.split(", ").length > 1 ? locationLabel.split(", ") : [locationLabel];
    }, [locationLabel]);

    const invoice = reservation?.invoice || {};
    const charges = Array.isArray(invoice.charges) ? invoice.charges : [];
    const taxes = Array.isArray(invoice.taxes) ? invoice.taxes : [];
    const extraCharges = Array.isArray(reservation?.extraCharge) ? reservation.extraCharge : [];
    const discountAmount = Number(reservation?.discount?.amount ?? invoice?.discount?.amount ?? 0);
    const depositAmount = Number(invoice.deposit || 0);
    const { estimatedTotal } = invoice;
    const extraMoney = Number(invoice.extraMoney || 0);
    const total = getInvoiceTotal(reservation);
    const totalPaid = getTotalPaid(reservation);
    const due = getInvoiceDue(reservation);

    return (
        <aside className="h-fit rounded-2xl border border-[#EEF0F6] bg-white shadow-[0_8px_24px_rgba(17,24,39,0.06)] lg:sticky lg:top-24">
            <div className="p-4 pb-0">
                {carImage ? (
                    <img src={carImage} alt={carName} className="h-44 w-full rounded-xl object-cover" draggable={false} />
                ) : (
                    <div className="flex h-44 w-full items-center justify-center rounded-xl bg-[#F6F7FB] text-sm text-[#98A2B3]">
                        {t("noImage")}
                    </div>
                )}
            </div>

            <div className="px-4 pb-5 pt-4">
                <h3 className="fontSemi text-[20px] capitalize leading-tight text-[#101828]">{carName}</h3>
                {plateNumber ? (
                    <p className="fontRegular mt-1 text-sm text-[#667085]">
                        {t("plateNumber")}: {plateNumber}
                    </p>
                ) : null}
                {categoryName && categoryName !== carName ? (
                    <p className="fontRegular mt-0.5 text-sm text-[#98A2B3]">{categoryName}</p>
                ) : null}

                <div className="mt-4 space-y-4 border-t border-[#EEF0F6] pt-4">
                    <div className="flex items-start justify-between gap-4">
                        <span className="fontSemi text-sm text-[#101828]">{t("pickupDate")}</span>
                        <span className="fontRegular text-right text-sm text-[#344054]">{formatTripDateTime(pickup)}</span>
                    </div>
                    <div className="flex items-start justify-between gap-4">
                        <span className="fontSemi text-sm text-[#101828]">{t("dropOffDate")}</span>
                        <span className="fontRegular text-right text-sm text-[#344054]">{formatTripDateTime(dropoff)}</span>
                    </div>
                    <div className="flex items-start justify-between gap-4">
                        <span className="fontSemi max-w-[42%] text-sm leading-5 text-[#101828]">
                            {t("pickupReturnLocation")}
                        </span>
                        <span className="fontRegular text-right text-sm leading-5 text-[#344054]">
                            {locationLines.map((line) => (
                                <span key={line} className="block">
                                    {line}
                                </span>
                            ))}
                        </span>
                    </div>
                </div>

                <div className="mt-4 space-y-2.5 border-t border-[#EEF0F6] pt-4">
                    {charges.map((item, index) => (
                        <div key={`charge-${index}`} className="flex items-start justify-between gap-4">
                            <span className="fontRegular text-sm capitalize text-[#667085]">
                                {item?.description}
                                {item?.unit != null ? ` x ${item.unit}` : ""}
                            </span>
                            <span className="fontSemi text-sm text-[#101828]">${formatMoney(item?.total)}</span>
                        </div>
                    ))}

                    <Divider className="!my-2" />

                    <div className="flex items-start justify-between gap-4">
                        <span className="fontRegular text-sm text-[#667085]">{t("estimatedTotal")}</span>
                        <span className="fontSemi text-sm text-[#101828]">
                            {estimatedTotal != null ? `$${formatMoney(Number(estimatedTotal))}` : "—"}
                        </span>
                    </div>

                    {discountAmount > 0 ? (
                        <div className="flex items-start justify-between gap-4">
                            <span className="fontRegular text-sm text-[#667085]">{t("discount")}</span>
                            <span className="fontSemi text-sm text-[#12B76A]">-${formatMoney(discountAmount)}</span>
                        </div>
                    ) : null}

                    {taxes.map((item, index) => (
                        <div key={`tax-${index}`} className="flex items-start justify-between gap-4">
                            <span className="fontRegular text-sm capitalize text-[#667085]">{item?.taxName}</span>
                            <span className="fontSemi text-sm text-[#101828]">+${formatMoney(item?.total)}</span>
                        </div>
                    ))}

                    {extraCharges.map((item, index) => (
                        <div key={`extra-${index}`} className="flex items-start justify-between gap-4">
                            <span className="fontRegular text-sm capitalize text-[#667085]">
                                {item?.description || item?.name || t("extraCharge")}
                            </span>
                            <span className="fontSemi text-sm text-[#101828]">
                                ${formatMoney(item?.total ?? item?.amount)}
                            </span>
                        </div>
                    ))}

                    {depositAmount > 0 && depositAmount !== totalPaid ? (
                        <div className="flex items-start justify-between gap-4">
                            <span className="fontRegular text-sm text-[#667085]">{t("deposit")}</span>
                            <span className="fontSemi text-sm text-[#101828]">${formatMoney(depositAmount)}</span>
                        </div>
                    ) : null}

                    {extraMoney > 0 ? (
                        <div className="flex items-start justify-between gap-4">
                            <span className="fontRegular text-sm text-[#667085]">{t("extraMoney")}</span>
                            <span className="fontSemi text-sm text-[#101828]">-${formatMoney(extraMoney)}</span>
                        </div>
                    ) : null}

                    <Divider className="!my-2" />

                    <div className="flex items-start justify-between gap-4">
                        <span className="fontSemi text-sm text-[#101828]">{t("invoiceTotal")}</span>
                        <span className="fontSemi text-sm text-[#101828]">${formatMoney(total)}</span>
                    </div>

                    <div className="flex items-center justify-between gap-4 pt-1">
                        <span className="fontSemi text-base text-[#101828]">{t("totalPaid")}</span>
                        <span className="fontSemi text-base text-[#5D5DF6]">${formatMoney(totalPaid)}</span>
                    </div>

                    <div className="flex items-center justify-between gap-4">
                        <span className="fontSemi text-base text-[#101828]">{t("amountDue")}</span>
                        <span className={`fontSemi text-base ${due > 0 ? "text-[#5D5DF6]" : "text-[#344054]"}`}>
                            ${formatMoney(due > 0 ? due : 0)}
                        </span>
                    </div>
                </div>
            </div>
        </aside>
    );
};

CheckInSummaryCard.propTypes = {
    reservation: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
};

export default CheckInSummaryCard;
